import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { CodelistPipe } from '@common/pipes/codelist.pipe';
import { GetVesselPipe } from '@common/pipes/getVessel.pipe';
import { BreezeService } from '@common/services/breeze.service';
import { Workbook, WorkbookSheetColumn, WorkbookSheetRow, WorkbookSheetRowCell } from '@progress/kendo-angular-excel-export';
import { saveAs } from '@progress/kendo-file-saver';
import _ from 'lodash';

@Injectable({
    providedIn: 'root',
    deps: [CodelistPipe, DatePipe, GetVesselPipe]
})
export class ExportService {
    constructor(
        private breezeService: BreezeService,
        private codelist: CodelistPipe,
        private datePipe: DatePipe,
        private getVesselPipe: GetVesselPipe
    ) { }

    public async transformExcelExport(workbook, data: any[], exportFileName = 'Export.xlsx', entityName: string, exportData: any) {
        const printDate = new Date();
        const sheet = workbook.sheets[0];
        sheet.freezePane = { rowSplit: 0, colSplit: 0 };
        const { rows, columns } = workbook.sheets[0] as { rows: WorkbookSheetRow[], columns: WorkbookSheetColumn[] };
        const headerOptions = rows[0].cells[0] as WorkbookSheetRowCell;
        const thousandsOptions = { format: '#,0.000' } as WorkbookSheetRowCell;

        if (exportData) {
            rows.splice(0, rows.length);
            columns.splice(0, columns.length);
        }

        const isArrival = exportData.type === 'Arrival';
        const dtoGridRows = [];
        data.forEach(element => dtoGridRows.push(this.breezeService.convertToDto(element)));

        if (entityName === 'VesselNotification') {
            let customHeaderCells: WorkbookSheetRowCell[] = [
                { value: `${exportData.type} details MSW`, bold: true, background: '#FFFF00', textAlign: 'center', colSpan: isArrival ? 23 : 19 },
            ];
            rows.unshift({ type: 'data', cells: customHeaderCells });
            rows.unshift({});

            if (exportData.year) {
                customHeaderCells = [
                    { value: 'Year:', bold: true },
                    { value: `${exportData.year}` },
                ];
                rows.unshift({ type: 'data', cells: customHeaderCells });

                customHeaderCells = [
                    { value: 'Month:', bold: true },
                    { value: exportData.month },
                ];
            }
            else {
                customHeaderCells = [
                    { value: exportData.dateLabelTo, bold: true },
                    { value: this.formatDate(exportData.completionDateTo) },
                ];
                rows.unshift({ type: 'data', cells: customHeaderCells });

                customHeaderCells = [
                    { value: exportData.dateLabelFrom, bold: true },
                    { value: this.formatDate(exportData.completionDateFrom) },
                ];
            }

            rows.unshift({ type: 'data', cells: customHeaderCells });
            rows.unshift({});

            customHeaderCells = [
                { value: exportData.type.toUpperCase(), bold: true }
            ];
            rows.unshift({ type: 'data', cells: customHeaderCells });

            const gridHeaderCells: WorkbookSheetColumn[] = [];
            if (isArrival) {
                gridHeaderCells.push(
                    Object.assign({}, headerOptions, { value: '№' }),
                    Object.assign({}, headerOptions, { value: 'Port of arrival' }),
                    Object.assign({}, headerOptions, { value: 'Vessel Name' }),
                    Object.assign({}, headerOptions, { value: 'IMO Number' }),
                    Object.assign({}, headerOptions, { value: 'Flag' }),
                    Object.assign({}, headerOptions, { value: 'Port of Registry' }),
                    Object.assign({}, headerOptions, { value: 'Gross Tonnage' }),
                    Object.assign({}, headerOptions, { value: 'Cargo (Weight)' }),
                    Object.assign({}, headerOptions, { value: 'Type of Ship' }),
                    Object.assign({}, headerOptions, { value: 'Last Port and Country' }),
                    Object.assign({}, headerOptions, { value: 'Nett tonage' }),
                    Object.assign({}, headerOptions, { value: 'Summer Displacement' }),
                    Object.assign({}, headerOptions, { value: 'Lenght Overall' }),
                    Object.assign({}, headerOptions, { value: 'Beam' }),
                    Object.assign({}, headerOptions, { value: 'Draft (fwd/aft)' }),
                    Object.assign({}, headerOptions, { value: 'Agent' }),
                    Object.assign({}, headerOptions, { value: 'Number of Crew' }),
                    Object.assign({}, headerOptions, { value: 'Number of Passengers' }),
                    Object.assign({}, headerOptions, { value: 'ETA (date time)' }),
                    Object.assign({}, headerOptions, { value: 'Next Port and Country' }),
                    Object.assign({}, headerOptions, { value: 'General Description of Cargo' }),
                    Object.assign({}, headerOptions, { value: 'Cargo Gross Weight' }),
                    Object.assign({}, headerOptions, { value: 'Dangerous Cargo on Board' })
                );
            } else {
                gridHeaderCells.push(
                    Object.assign({}, headerOptions, { value: '№' }),
                    Object.assign({}, headerOptions, { value: 'Port of Depature' }),
                    Object.assign({}, headerOptions, { value: 'Vessel Name' }),
                    Object.assign({}, headerOptions, { value: 'IMO Number' }),
                    Object.assign({}, headerOptions, { value: 'Flag' }),
                    Object.assign({}, headerOptions, { value: 'Port of Registry' }),
                    Object.assign({}, headerOptions, { value: 'Gross Tonnage' }),
                    Object.assign({}, headerOptions, { value: 'Type of Ship' }),
                    Object.assign({}, headerOptions, { value: 'Port and Country of Destination' }),
                    Object.assign({}, headerOptions, { value: 'Date and Time of Depature' }),
                    Object.assign({}, headerOptions, { value: 'Number of Crew' }),
                    Object.assign({}, headerOptions, { value: 'Number of Passengers' }),
                    Object.assign({}, headerOptions, { value: 'Total Person on Board' }),
                    Object.assign({}, headerOptions, { value: 'Boarded Passengers' }),
                    Object.assign({}, headerOptions, { value: 'Disambarked Passengers' }),
                    Object.assign({}, headerOptions, { value: 'Cargo Loaded' }),
                    Object.assign({}, headerOptions, { value: 'Cargo Disharged' }),
                    Object.assign({}, headerOptions, { value: 'Total Cargo on Depature' }),
                    Object.assign({}, headerOptions, { value: 'IMDG Cargo on Board' })
                );
            }
            gridHeaderCells.forEach((cell, i) => columns.push({ autoWidth: false, index: i, width: 10 }));

            rows.push({ type: 'header', cells: gridHeaderCells });

            for (let i = 0; i < dtoGridRows.length; i++) {
                const dataCells: WorkbookSheetRowCell[] = [];
                if (isArrival) {
                    dataCells.push(
                        { value: i + 1 },
                        { value: await this.codelist.transform(dtoGridRows[i].portOfCallId, 'Location') },
                        { value: await this.codelist.transform(dtoGridRows[i].vesselId, 'Vessel') },
                        { value: dtoGridRows[i].imoNumber },
                        { value: await this.codelist.transform(dtoGridRows[i].countryId, 'Country') },
                        { value: dtoGridRows[i].portOfRegistry },
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].grossTonnage }),
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].cargoGrossWeight }),
                        { value: await this.codelist.transform(dtoGridRows[i].vesselTypeId, 'VesselType') },
                        { value: await this.codelist.transform(dtoGridRows[i].lastPortId, 'Location') },
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].netTonnage }),
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].deadWeight }),
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].length }),
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].lengthBetweenPerpendiculars }),
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].draft }),
                        { value: await this.codelist.transform(dtoGridRows[i].agentId, 'Organization') },
                        { value: dtoGridRows[i].numberOfCrew },
                        { value: dtoGridRows[i].numberOfPax },
                        { value: this.formatDate(dtoGridRows[i].portOfCallEta) },
                        { value: await this.codelist.transform(dtoGridRows[i].nextPortId, 'Location') },
                        { value: dtoGridRows[i].cargoDescripction },
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].cargoGrossWeight }),
                        { value: dtoGridRows[i].dpgOnBoard ? 'Yes' : 'No' }
                    );
                } else {
                    dataCells.push(
                        { value: i + 1 },
                        { value: await this.codelist.transform(dtoGridRows[i].portOfCallId, 'Location') },
                        { value: await this.codelist.transform(dtoGridRows[i].vesselId, 'Vessel') },
                        { value: dtoGridRows[i].imoNumber },
                        { value: await this.codelist.transform(dtoGridRows[i].countryId, 'Country') },
                        { value: dtoGridRows[i].portOfRegistry },
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].grossTonnage }),
                        { value: await this.codelist.transform(dtoGridRows[i].vesselTypeId, 'VesselType') },
                        { value: await this.codelist.transform(dtoGridRows[i].nextPortId, 'Location') },
                        { value: this.formatDate(dtoGridRows[i].completionDate) },
                        { value: dtoGridRows[i].numberOfCrew },
                        { value: dtoGridRows[i].numberOfPax },
                        { value: dtoGridRows[i].personsOnBoard },
                        { value: dtoGridRows[i].embarkationPassengers },
                        { value: dtoGridRows[i].disembarkationPassengers },
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].cargoLoaded }),
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].cargoDischarged }),
                        Object.assign({}, thousandsOptions, { value: dtoGridRows[i].totalCargo }),
                        { value: dtoGridRows[i].dpgOnBoard ? 'Yes' : 'No' }
                    );
                }

                rows.push({ type: 'data', cells: dataCells });
            }
        }
        else {
            const startIndex = rows.findIndex(o => o.type === 'header') + 1;
            for (let i = startIndex; i < rows.length; i++) {
                for (let j = 0; j < rows[startIndex].cells.length; j++) {
                    const cell = rows[i].cells[j];
                    const codelistKey = this.isCodeList(cell.value, dtoGridRows[startIndex - i]);
                    const columnTitle = rows[startIndex - 1].cells[j].value as string;
                    if (columnTitle !== 'ID') await this.formatCell(cell, codelistKey, entityName);
                }
            }
        }

        const startIndex = rows.findIndex(o => o.type === 'header');
        // Override default autosize width
        if (rows.length > 1) this.setAutoColumnWidths(rows, columns, startIndex);

        new Workbook(workbook).toDataURL().then((dataUrl: string) => saveAs(dataUrl, `${exportFileName}_${this.datePipe.transform(printDate, 'dd-MM-yyyy')}.xlsx`));
    }

    private async formatCell(cell: any, codelistKey: string, entityName: string) {
        // Format codelists
        if (['number', 'string'].includes(typeof cell.value) && codelistKey) {
            let codelistValue = await this.codelist.transform(cell.value, `${entityName}${codelistKey}`);
            if (codelistValue === '-') codelistValue = await this.codelist.transform(cell.value, codelistKey);
            if (codelistValue !== '-') cell.value = codelistValue;
        }
        // Format date
        if (typeof cell.value === 'object' && !!cell.value && !isNaN(Date.parse(cell.value))) {
            cell.value = this.formatDate(cell.value);
            cell.value = cell.value.substring(0, cell.value.length - 3);
        }
        // Format empty array
        if (typeof cell.value === 'object' && cell.value?.length === 0) {
            cell.value = '';
        }
        // Format invoices array
        if (typeof cell.value === 'object' && cell.value?.length > 0) {
            cell.value = cell.value[cell.value.length - 1].invoiceNumber;
        }
        // Format boolean
        if (typeof cell.value === 'boolean') {
            cell.value = cell.value ? '  ✓  ' : '';
        }
    }

    private formatDate(date: Date): string {
        if (!date) return '';
        return `${new Date(date).toLocaleDateString('en-SI')} ${new Date(date).toLocaleTimeString('en-SI')}`;
    }

    private formatDecimal(number: Number): string {
        if (!number) return '';
        return number.toLocaleString('en-SI', { maximumFractionDigits: 3, minimumFractionDigits: 3 });
    }

    private setColumnWidths(columns: WorkbookSheetColumn[], width: number): void {
        columns.forEach(column => {
            column.autoWidth = false;
            column.width = width;
        });
    }

    private setAutoColumnWidths(rows: WorkbookSheetRow[], columns: WorkbookSheetColumn[], startRow = 1): void {
        const allLengths = [];
        for (let i = 0; i < rows[startRow].cells.length; i++) {
            const lengths = [];
            for (const row of rows) {
                const cellValue = row.cells ? row.cells[i]?.value : {};
                if (typeof cellValue === 'string') {
                    lengths.push(cellValue.length);
                }
                else if (typeof cellValue === 'number') {
                    lengths.push(cellValue.toString().length);
                }
                else {
                    lengths.push(5);
                }
            }
            allLengths.push(lengths);
        }

        const maxLengths = [];
        allLengths.forEach(col => maxLengths.push(Math.max(...col)));

        for (const col in columns) {
            if (!col) continue;
            columns[col].autoWidth = false;
            columns[col].width = maxLengths[col] <= 15 ? maxLengths[col] * 9 : maxLengths[col] * 7;
        }
    }

    private isCodeList(cellValue: any, gridRow: any): string {
        // Only checks one level
        for (const [key, value] of Object.entries(gridRow)) {
            if (['number', 'string'].includes(typeof value) && value === cellValue && key.substring(key.length - 2) === 'Id' && key.length > 2) {
                const keyString: string = key.substring(0, key.length - 2);
                return keyString.charAt(0).toUpperCase() + keyString.slice(1);
            }
        }
        return null;
    }
}
